import { Flex } from '@mantine/core'
import { createFormContext, zodResolver } from '@mantine/form'
import { useDebouncedState } from '@mantine/hooks'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  useQueryGetEstimateInsuranceForItem,
  useQueryGetEstimateInsuranceForQuote,
} from '@flecto/core/saas'
import {
  type CoreEstimateInsuranceForItem,
  type CoreEstimateInsuranceForQuote,
  type CoreInsurance,
  type CoreInsurancePayload,
} from '@flecto/types'

import useInsuranceState from '../../../insurances/hooks/useInsuranceState'
import { useModalConfirmationCreateInsurance } from '../../../insurances/hooks/useModalConfirmationCreateInsurance'
import { useTaxesInfo } from '../../../taxes'
import { CardCustomerSelect } from '../CardCustomerSelect/CardCustomerSelect'
import { CardProductSelect } from '../CardProductSelect/CardProductSelect'
import { CardSelectCoveragePeriod } from '../CardSelectCoveragePeriod/CardSelectCoveragePeriod'
import { InsuranceDetails } from '../InsuranceDetails/InsuranceDetails'
import { useFormCreateInsurancePolicySchema } from './useFormCreateInsurancePolicySchema'

export const [FormProvider, useFormContext, useForm] = createFormContext<
  Partial<
    CoreInsurancePayload & {
      customer: CoreInsurance['customer']
      item: CoreInsurance['item']
      start_at_now: boolean
      price_cents: number
    }
  >
>()

type FormCreateInsurancePolicyProps = {
  id: string
  setEnableButton?: (value: boolean) => void
}

export function FormCreateInsurancePolicy({
  setEnableButton,
  ...props
}: FormCreateInsurancePolicyProps) {
  const [localPrice, setLocalPrice] = useDebouncedState(0, 2500)
  const [localQuantity, setLocalQuantity] = useDebouncedState(1, 500)
  const [isPriceManuallyChanged, setIsPriceManuallyChanged] = useState(false)
  const [hasDeletedProduct, setHasDeletedProduct] = useState(false)
  const [resetRentalValue, setResetRentalValue] = useState(false)
  const [rentalValue, setRentalValue] = useState<number>(0)
  const [productInitialValues, setProductInitialValues] = useState<
    Partial<CoreInsurance['item']>
  >({})
  const { userSettingShowPricesWithTaxes } = useTaxesInfo()
  const { schema, initialValues } = useFormCreateInsurancePolicySchema()

  const { customer, start_at, end_at } = useInsuranceState()

  const router = useRouter()

  const form = useForm({
    validate: zodResolver(schema),
    initialValues,
  })

  const { openModal } = useModalConfirmationCreateInsurance({
    form: form.values as CoreInsurancePayload & {
      customer: CoreInsurance['customer']
      item: CoreInsurance['item']
      start_at_now: boolean
      price_cents: number
    },
  })

  const {
    data: estimate,
    refetch: refetchEstimate,
    isLoading: isLoadingEstimate,
    isRefetching: isRefetchingEstimate,
  } = useQueryGetEstimateInsuranceForItem({
    item_internal_id: form.values.item?.internal_id ?? '',
    options: {
      start_at: form.values.start_at ?? '',
      end_at: form.values.end_at ?? '',
      quantity:
        localQuantity > 0 ? localQuantity : (form.values.item?.quantity ?? 0),
    },
  })

  const isQuoteEnabled =
    !!form.values.item?.internal_id &&
    ((!!estimate && !hasDeletedProduct) ||
      (isPriceManuallyChanged && localPrice > 0))

  const getCorrectRentalValue = useMemo(() => {
    if (isPriceManuallyChanged && localPrice > 0) {
      return Math.round(localPrice)
    }

    if (estimate) {
      const currentEstimateValue = userSettingShowPricesWithTaxes
        ? estimate.unit_rental_with_vat_cents
        : estimate.unit_rental_cents

      if (rentalValue !== currentEstimateValue) {
        setRentalValue(currentEstimateValue)
      }

      return Math.round(currentEstimateValue)
    }

    return Math.round(rentalValue)
  }, [isPriceManuallyChanged, localPrice, estimate, rentalValue])

  const {
    data: estimateQuote,
    refetch: refetchQuote,
    isRefetching: isLoadingEstimateQuote,
  } = useQueryGetEstimateInsuranceForQuote({
    item_internal_id: form.values.item?.internal_id ?? '',
    options: {
      start_at: form.values.start_at ?? '',
      end_at: form.values.end_at ?? '',
      quantity: localQuantity > 0 ? localQuantity : (form.values.quantity ?? 0),
      unit_incidence_value:
        isPriceManuallyChanged && localPrice > 0
          ? Math.round(localPrice)
          : userSettingShowPricesWithTaxes
            ? Math.round(estimate?.unit_rental_with_vat_cents ?? 0)
            : Math.round(estimate?.unit_rental_cents ?? 0),
    },
    enabled: isQuoteEnabled && getCorrectRentalValue > 0,
  })

  const hasAllValues = useMemo(() => {
    return (
      form?.values?.item_id &&
      (form?.values?.incidence_value_cents ?? 0 > 0) &&
      (form?.values?.price_cents ?? 0 > 0) &&
      (form?.values?.quantity ?? 0) > 0 &&
      form.values.customer_id &&
      form.values.start_at &&
      form.values.end_at
    )
  }, [form.values])

  async function saveInsurancePolicy() {
    openModal()
  }

  useEffect(() => {
    if (estimate) {
      const newRentalValue = userSettingShowPricesWithTaxes
        ? estimate.unit_rental_with_vat_cents
        : estimate.unit_rental_cents

      if (!isPriceManuallyChanged) {
        setRentalValue(Math.round(newRentalValue))
        setHasDeletedProduct(false)
      }
    }
  }, [estimate, userSettingShowPricesWithTaxes, isPriceManuallyChanged])

  useEffect(() => {
    if (localQuantity > 0) {
      form.setFieldValue('quantity', localQuantity)

      if (form.values.item?.internal_id) {
        refetchEstimate()
      }
    }
  }, [localQuantity])

  useEffect(() => {
    form.setValues(initialValues)
  }, [])

  useEffect(() => {
    if (form.values.item?.internal_id) {
      if (form.values.item?.internal_id !== productInitialValues?.internal_id) {
        setIsPriceManuallyChanged(false)
        setLocalPrice(0)

        setProductInitialValues(form.values.item)
      }

      refetchEstimate()
    }
  }, [
    form.values.item?.internal_id,
    resetRentalValue,
    form.values.start_at_now,
    form.values.start_at,
    form.values.end_at,
  ])

  useEffect(() => {
    if (estimate && form.values.item?.internal_id) {
      const newRentalValue = userSettingShowPricesWithTaxes
        ? estimate.unit_rental_with_vat_cents
        : estimate.unit_rental_cents

      if (!isPriceManuallyChanged) {
        setRentalValue(Math.round(newRentalValue))
      }

      refetchQuote()

      setHasDeletedProduct(false)
    }
  }, [estimate, form.values.item?.internal_id])

  useEffect(() => {
    if (
      form.values.item?.internal_id &&
      (isPriceManuallyChanged || localQuantity > 0)
    ) {
      refetchQuote()
    }
  }, [localPrice, localQuantity, isPriceManuallyChanged])

  const handlePriceChange = useCallback((value: number) => {
    setIsPriceManuallyChanged(true)
    setLocalPrice(Math.round(value))
    form.setFieldValue('incidence_value_cents', Math.round(value))
  }, [])

  const handleResetValues = useCallback(() => {
    setIsPriceManuallyChanged(false)
    setLocalPrice(0)
    if (estimate) {
      const newRentalValue = userSettingShowPricesWithTaxes
        ? estimate.unit_rental_with_vat_cents
        : estimate.unit_rental_cents
      setRentalValue(Math.round(newRentalValue))
    }
  }, [estimate, userSettingShowPricesWithTaxes])

  const handleDeleteProduct = () => {
    setHasDeletedProduct(true)
    form.setFieldValue('item', undefined)
    form.setFieldValue('item_id', undefined)
    setProductInitialValues({})
    setLocalPrice(0)
    setRentalValue(0)
    setIsPriceManuallyChanged(false)
    setLocalQuantity(1)
  }

  const handleItemSelection = (field: string, value: CoreInsurance['item']) => {
    setHasDeletedProduct(false)
    setIsPriceManuallyChanged(false)
    setLocalPrice(0)

    form.setFieldValue(field, value)
  }

  useEffect(() => {
    if (resetRentalValue) {
      form.setFieldValue('item', productInitialValues)
      setIsPriceManuallyChanged(false)
      setHasDeletedProduct(false)
      handleResetValues()
      setResetRentalValue(false)
    }
  }, [resetRentalValue])

  useEffect(() => {
    if (hasAllValues) {
      setEnableButton?.(true)
    } else {
      setEnableButton?.(false)
    }
  }, [hasAllValues, form.values])

  useEffect(() => {
    if (estimateQuote?.price_cents) {
      form.setFieldValue('price_cents', estimateQuote.price_cents)
    }
  }, [estimateQuote])

  useEffect(() => {
    if (router.query.use_same_customer_and_dates) {
      form.setValues({
        start_at_now: false,
        customer: customer as CoreInsurance['customer'],
        start_at: start_at as CoreInsurance['start_at'],
        end_at: end_at as CoreInsurance['end_at'],
      })
    }
  }, [])

  return (
    <FormProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => saveInsurancePolicy())}>
        <Flex direction='column' gap='lg'>
          <InsuranceDetails
            estimate={estimate as CoreEstimateInsuranceForItem}
            estimateQuote={estimateQuote as CoreEstimateInsuranceForQuote}
            rentalValue={getCorrectRentalValue}
            localPrice={localPrice}
            isRefetchingEstimate={isRefetchingEstimate}
            isLoadingEstimateQuote={isLoadingEstimateQuote}
          />
          <CardCustomerSelect />
          <CardSelectCoveragePeriod />
          <CardProductSelect
            estimateQuote={estimateQuote as CoreEstimateInsuranceForQuote}
            productState={{
              localPrice,
              localQuantity,
              isPriceManuallyChanged,
              rentalValue: getCorrectRentalValue,
              isRefetchingEstimate,
              isLoadingEstimate,
              isLoadingEstimateQuote,
            }}
            productActions={{
              setLocalPrice: handlePriceChange,
              setLocalQuantity,
              deleteProduct: handleDeleteProduct,
              handleItemSelection,
              setResetRentalValue,
              setProductInitialValues,
            }}
          />
        </Flex>
      </form>
    </FormProvider>
  )
}
